import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import styled from 'styled-components';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  customizationBelongsToInboxFilter,
  customizationBuyerName,
  customizationsAPI,
  formatCustomizationMoney,
} from '../../api/customizationsAPI';

const Page = styled.main`max-width: 980px; margin: 0 auto; padding: 2rem 1rem 5rem;`;
const Title = styled.h1`color: var(--text-primary); font-size: 2rem; margin: 0 0 1.25rem;`;
const Filters = styled.div`display: flex; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 1.5rem;`;
const Chip = styled.button`
  background: ${(p) => p.$active ? '#1b4332' : 'var(--bg-primary, #fff)'};
  border: 1px solid ${(p) => p.$active ? '#1b4332' : 'var(--border-color, #d1d5db)'};
  border-radius: 999px;
  color: ${(p) => p.$active ? '#fff' : 'var(--text-primary)'};
  cursor: pointer;
  font-weight: 700;
  padding: 0.55rem 1rem;
`;
const List = styled.div`display: grid; gap: 1rem;`;
const Card = styled(Link)`
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 0.8rem;
  box-shadow: var(--shadow-sm);
  color: inherit;
  padding: 1.25rem;
  text-decoration: none;
  &:hover { box-shadow: var(--shadow-md); transform: translateY(-1px); }
`;
const Header = styled.div`align-items: flex-start; display: flex; gap: 1rem; justify-content: space-between;`;
const Name = styled.h2`font-size: 1.05rem; margin: 0;`;
const DateText = styled.div`color: var(--text-secondary); font-size: 0.8rem; margin-top: 0.25rem;`;
const Badge = styled.span`
  background: #e8f3ec;
  border-radius: 999px;
  color: #1b4332;
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.4rem 0.65rem;
`;
const Size = styled.div`color: #1b4332; font-size: 1.05rem; font-weight: 800; margin-top: 1rem;`;
const Meta = styled.div`color: var(--text-secondary); font-size: 0.875rem; margin-top: 0.35rem;`;
const LinkText = styled.div`color: #1b4332; font-weight: 800; margin-top: 0.9rem;`;
const State = styled.div`
  background: var(--bg-primary, #fff);
  border-radius: 0.75rem;
  padding: 3rem 1.5rem;
  text-align: center;
`;
const Retry = styled.button`
  background: #1b4332; border: 0; border-radius: 0.5rem; color: #fff;
  cursor: pointer; font-weight: 700; margin-top: 1rem; padding: 0.7rem 1.1rem;
`;

const filters = [
  { id: 'needs_response', label: 'Needs response' },
  { id: 'active', label: 'Active' },
  { id: 'closed', label: 'Closed' },
];
const statusCopy = {
  sent: 'New request',
  quoted: 'Quote sent',
  accepted: 'Quote accepted',
  converted: 'Ordered',
  cancelled: 'Cancelled by buyer',
  expired: 'Expired',
};
const emptyCopy = {
  needs_response: ['No new customization requests', 'New requests will appear here.'],
  active: ['No active customization requests.', 'Quotes and accepted requests will appear here.'],
  closed: ['No closed customization requests.', 'Completed and closed requests will appear here.'],
};
const ArtistCustomizationInboxPage = () => {
  const [filter, setFilter] = useState('needs_response');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await customizationsAPI.getArtistInbox();
      setRequests(data.results);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const visible = useMemo(
    () => requests.filter((request) => customizationBelongsToInboxFilter(request.state, filter)),
    [filter, requests],
  );

  if (loading && !requests.length) return <LoadingSpinner text="Loading customization requests…" />;

  return (
    <Page>
      <Title>Customization requests</Title>
      <Filters role="tablist" aria-label="Filter customization requests">
        {filters.map((item) => (
          <Chip
            key={item.id}
            type="button"
            role="tab"
            aria-selected={filter === item.id}
            $active={filter === item.id}
            onClick={() => setFilter(item.id)}
          >
            {item.label}
          </Chip>
        ))}
      </Filters>
      {error ? (
        <State>
          <strong>We couldn’t load customization requests.</strong>
          <div><Retry type="button" onClick={load}>Try again</Retry></div>
        </State>
      ) : visible.length === 0 ? (
        <State><h2>{emptyCopy[filter][0]}</h2><p>{emptyCopy[filter][1]}</p></State>
      ) : (
        <List>
          {visible.map((request) => (
            <Card key={request.id} to={`/artist/customization-requests/${request.id}`}>
              <Header>
                <div>
                  <Name>{customizationBuyerName(request)}</Name>
                  <DateText>{new Date(request.updated_at || request.sent_at || request.created_at).toLocaleDateString()}</DateText>
                </div>
                <Badge>{statusCopy[request.state] || request.state}</Badge>
              </Header>
              <Size>{request.painting_brief?.width_cm || '—'} × {request.painting_brief?.height_cm || '—'} cm</Size>
              <Meta>Budget {formatCustomizationMoney(request.budget_min_cents)} – {formatCustomizationMoney(request.budget_max_cents)}</Meta>
              <Meta>{request.references?.length || 0} reference image{request.references?.length === 1 ? '' : 's'}</Meta>
              <LinkText>{request.state === 'sent' ? 'Review request' : 'View request'} →</LinkText>
            </Card>
          ))}
        </List>
      )}
    </Page>
  );
};

export default ArtistCustomizationInboxPage;
